import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock3, User as UserIcon, Eye } from "lucide-react";
import { Header, Footer, Loading } from "../components/Shell";
import { api, rupiah } from "../lib/api";
import { useSeo } from "../lib/seo";

export function BlogIndex() {
  const [posts, setPosts] = useState([]);
  useSeo({
    title: "Blog — Insight Digital Marketing untuk Seller Indonesia",
    description: "Panduan lengkap Meta Ads, TikTok Ads, Shopee Ads, Landing Page, dan strategi digital marketing praktis dari tim MASTOFA.",
    path: "/blog",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Blog MASTOFA.COM",
      description: "Insight digital marketing untuk seller & advertiser Indonesia.",
      inLanguage: "id-ID",
    },
  });
  useEffect(() => {
    api("/blog").then(setPosts).catch(() => {});
  }, []);
  return (
    <div>
      <Header />
      <main className="page">
        <div className="page-intro">
          <span className="section-kicker">BLOG MASTOFA</span>
          <h1>
            Insight praktis untuk<br />
            <em>marketer &amp; seller Indonesia.</em>
          </h1>
          <p>Panduan lengkap Meta Ads, TikTok Ads, Shopee Ads dari tim MASTOFA.</p>
        </div>
        <div className="blog-grid" data-testid="blog-grid">
          {posts.length === 0 && (
            <div className="empty-inline">Belum ada artikel.</div>
          )}
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className="blog-card"
              data-testid={`blog-card-${p.slug}`}
            >
              {p.thumbnail && <img src={p.thumbnail} alt={p.title} />}
              <div className="blog-card-body">
                <div className="blog-tags">
                  {(p.tags || []).slice(0, 2).map((t) => (
                    <span key={t}>#{t}</span>
                  ))}
                </div>
                <h3>{p.title}</h3>
                <p>{p.excerpt}</p>
                <div className="blog-meta">
                  <span><UserIcon size={13} /> {p.author}</span>
                  <span><Eye size={13} /> {p.views || 0}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");
  useSeo({
    title: post ? (post.seo_title || post.title) : "Artikel",
    description: post ? (post.seo_description || post.excerpt) : "Artikel digital marketing MASTOFA.",
    path: `/blog/${slug}`,
    image: post?.thumbnail,
    type: "article",
    jsonLd: post
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.thumbnail,
          author: { "@type": "Person", name: post.author },
          datePublished: post.published_at,
          publisher: { "@type": "Organization", name: "MASTOFA.COM", url: "https://mastofa.com" },
          mainEntityOfPage: { "@type": "WebPage", "@id": `https://mastofa.com/blog/${slug}` },
          keywords: (post.tags || []).join(", "),
          inLanguage: "id-ID",
        }
      : undefined,
  });
  useEffect(() => {
    api(`/blog/${slug}`)
      .then(setPost)
      .catch((e) => setErr(e.response?.data?.detail || "Artikel tidak ditemukan."));
  }, [slug]);
  if (err) {
    return (
      <div>
        <Header />
        <main className="page">
          <p>{err}</p>
          <Link to="/blog" className="button">Kembali ke blog</Link>
        </main>
      </div>
    );
  }
  if (!post) return <Loading label="Memuat artikel..." />;
  return (
    <div>
      <Header />
      <main className="page blog-post-page">
        <Link to="/blog" className="back-link">← Semua artikel</Link>
        <div className="blog-tags" data-testid="post-tags">
          {(post.tags || []).map((t) => <span key={t}>#{t}</span>)}
        </div>
        <h1 data-testid="post-title">{post.title}</h1>
        <div className="blog-meta blog-meta-large">
          <span><UserIcon size={14} /> {post.author}</span>
          <span><Clock3 size={14} /> {new Date(post.published_at).toLocaleDateString("id-ID")}</span>
          <span><Eye size={14} /> {post.views || 0} pembaca</span>
        </div>
        {post.thumbnail && (
          <img src={post.thumbnail} alt={post.title} className="post-hero-img" />
        )}
        <article
          className="post-body"
          data-testid="post-body"
          dangerouslySetInnerHTML={{ __html: post.content_html }}
        />
        {post.related?.length ? (
          <section className="related-courses">
            <span className="section-kicker">KELAS TERKAIT</span>
            <h2>Pelajari lebih dalam</h2>
            <div className="related-grid">
              {post.related.map((c) => (
                <Link key={c.slug} to={`/courses/${c.slug}`} className="related-card">
                  {c.thumbnail && <img src={c.thumbnail} alt={c.title} />}
                  <div>
                    <b>{c.title}</b>
                    <span>{rupiah(c.price)}</span>
                    <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
